import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

import confetti from 'canvas-confetti';
import { getToken } from '../../services/tokenService';
import { API, FTPE_DOCUMENTS_DOWNLOAD, WORKFLOW_STEPS } from './ftpeConstants';

export function useFtpeWorkflow() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  // État du workflow
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [adhesionId, setAdhesionId] = useState(null);
  const [error, setError] = useState('');
  
  // Données du formulaire
  const [formData, setFormData] = useState({
    raison_sociale: '',
    nom_commercial: '',
    forme_juridique: '',
    siret: '',
    code_ape: '',
    date_creation: '',
    adresse: '',
    territoire: 'Guadeloupe',
    telephone: '',
    email: '',
    site_web: '',
    representant_nom: '',
    representant_fonction: '',
    representant_email: '',
    representant_telephone: '',
    type_adhesion: 'tpe',
    autorisation_communication: true,
    reception_informations: true
  });
  
  // État de la signature
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [lieuSignature, setLieuSignature] = useState('');
  const [debugCode, setDebugCode] = useState(null);
  
  // État du paiement
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  // État pour la sauvegarde brouillon
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  const [formModified, setFormModified] = useState(false);
  
  // Temps estimé par étape (en minutes)
  const STEP_DURATION = {
    1: 2,  // Document Maître
    2: 1,  // Présentation
    3: 5,  // Informations
    4: 3,  // Signature
    5: 2,  // Paiement
    6: 0   // Confirmation
  };
  
  // Calculer le temps restant estimé
  const getEstimatedTimeRemaining = () => {
    let totalMinutes = 0;
    for (let i = currentStep; i <= 5; i++) {
      totalMinutes += STEP_DURATION[i] || 0;
    }
    return totalMinutes;
  };
  
  // Calculer le pourcentage de progression
  const getProgressPercentage = () => {
    const completedSteps = currentStep - 1;
    return Math.round((completedSteps / 5) * 100);
  };
  
  const token = getToken();
  const stepFromUrl = searchParams.get('step');
  const adhesionIdFromUrl = searchParams.get('adhesion_id');
  const sessionIdFromUrl = searchParams.get('session_id');
  const draftIdFromUrl = searchParams.get('draft_id');
  
  // Paramètres pour pré-remplir depuis l'espace COOPER (adhésion pour un membre)
  const memberIdFromUrl = searchParams.get('member_id');
  const memberNameFromUrl = searchParams.get('member_name');
  const memberEmailFromUrl = searchParams.get('member_email');
  const memberPhoneFromUrl = searchParams.get('member_phone');
  const memberEntrepriseFromUrl = searchParams.get('member_entreprise');
  const memberTerritoireFromUrl = searchParams.get('member_territoire');
  const forMemberMode = searchParams.get('for_member') === 'true';

  // Charger un brouillon existant
  const loadDraft = useCallback(async (id) => {
    try {
      const response = await axios.get(`${API}/ftpe-adhesion/workflow/draft/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        const draft = response.data;
        setDraftId(draft.id);
        setFormData(prev => ({ ...prev, ...draft.form_data }));
        setCurrentStep(draft.current_step || 1);
        toast.success(`Brouillon chargé - ${draft.days_remaining} jours restants`);
      }
    } catch {
      toast.error('Impossible de charger le brouillon');
    }
  }, [token]);

  // Charger l'état depuis l'URL
  useEffect(() => {
    if (draftIdFromUrl) {
      loadDraft(draftIdFromUrl);
    } else if (adhesionIdFromUrl) {
      setAdhesionId(adhesionIdFromUrl);
    }
    if (stepFromUrl === 'success' && sessionIdFromUrl) {
      setCurrentStep(6);
      checkPaymentStatus(adhesionIdFromUrl);
    } else if (stepFromUrl === 'payment' && adhesionIdFromUrl) {
      setCurrentStep(5);
    }
    
    // Pré-remplir les données si on vient de l'espace COOPER pour un membre
    if (forMemberMode && memberNameFromUrl) {
      setFormData(prev => ({
        ...prev,
        raison_sociale: memberEntrepriseFromUrl ? decodeURIComponent(memberEntrepriseFromUrl) : '',
        representant_nom: memberNameFromUrl ? decodeURIComponent(memberNameFromUrl) : '',
        email: memberEmailFromUrl ? decodeURIComponent(memberEmailFromUrl) : '',
        representant_email: memberEmailFromUrl ? decodeURIComponent(memberEmailFromUrl) : '',
        telephone: memberPhoneFromUrl ? decodeURIComponent(memberPhoneFromUrl) : '',
        representant_telephone: memberPhoneFromUrl ? decodeURIComponent(memberPhoneFromUrl) : '',
        territoire: memberTerritoireFromUrl ? decodeURIComponent(memberTerritoireFromUrl) : 'Guadeloupe'
      }));
    }
  }, [draftIdFromUrl, adhesionIdFromUrl, stepFromUrl, sessionIdFromUrl, forMemberMode, memberNameFromUrl, memberEmailFromUrl, memberPhoneFromUrl, memberEntrepriseFromUrl, memberTerritoireFromUrl, loadDraft]);

  // Auto-sauvegarde toutes les 30 secondes si le formulaire a été modifié (étape 3)
  useEffect(() => {
    if (!autoSaveEnabled || currentStep !== 3 || !formModified || !token) return;
    
    const autoSaveInterval = setInterval(async () => {
      if (formModified && formData.raison_sociale) {
        try {
          setSavingDraft(true);
          const response = await axios.post(
            `${API}/ftpe-adhesion/workflow/save-draft`,
            {
              draft_id: draftId,
              form_data: formData,
              current_step: currentStep
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.success) {
            setDraftId(response.data.draft_id);
            setLastSaved(new Date());
            setFormModified(false);
          }
        } catch {
        } finally {
          setSavingDraft(false);
        }
      }
    }, 30000); // 30 secondes
    
    return () => clearInterval(autoSaveInterval);
  }, [autoSaveEnabled, currentStep, formModified, formData, draftId, token]);

  // Marquer le formulaire comme modifié quand les données changent
  useEffect(() => {
    if (currentStep === 3) {
      setFormModified(true);
    }
  }, [formData, currentStep]);

  // Avertissement avant de quitter la page si le formulaire n'est pas sauvegardé
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (formModified && currentStep >= 3 && currentStep < 5) {
        e.preventDefault();
        e.returnValue = 'Vous avez des modifications non sauvegardées. Êtes-vous sûr de vouloir quitter ?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formModified, currentStep]);

  const checkPaymentStatus = async (aid) => {
    if (!aid) return;
    
    try {
      const response = await axios.get(`${API}/ftpe-adhesion/workflow/${aid}/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.status === 'completed' || response.data.payment_status === 'paid') {
        setPaymentStatus('completed');
        toast.success('🎉 Adhésion FTPE confirmée !');
        // Célébration avec confetti
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        // Double confetti après 500ms
        setTimeout(() => {
          confetti({
            particleCount: 50,
            angle: 60,
            spread: 55,
            origin: { x: 0 }
          });
          confetti({
            particleCount: 50,
            angle: 120,
            spread: 55,
            origin: { x: 1 }
          });
        }, 500);
      } else if (response.data.payment_status === 'pending_virement') {
        setPaymentStatus('pending_virement');
      } else {
        setPaymentStatus('pending');
        // Retry after 2 seconds
        setTimeout(() => checkPaymentStatus(aid), 2000);
      }
    } catch {
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Sauvegarder le brouillon pour reprise ultérieure
  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const response = await axios.post(
        `${API}/ftpe-adhesion/workflow/save-draft`,
        {
          draft_id: draftId,
          form_data: formData,
          current_step: currentStep
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setDraftId(response.data.draft_id);
        setLastSaved(new Date());
        toast.success(`Brouillon sauvegardé - Valable ${response.data.days_remaining} jours`);
      }
    } catch {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingDraft(false);
    }
  };

  // Télécharger un document officiel FTPE
  const handleDownloadDocument = async (docType) => {
    try {
      const response = await axios.get(
        FTPE_DOCUMENTS_DOWNLOAD[docType],
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const filename = {
        statuts: 'statuts_ftpe_outremer_2025.pdf',
        reglement: 'reglement_interieur_ftpe_2025.pdf',
        charte: 'charte_ethique_ftpe_2025.pdf'
      }[docType];
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Téléchargement de ${filename}`);
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Prévisualiser le bulletin d'adhésion avant signature
  const [previewingBulletin, setPreviewingBulletin] = useState(false);
  
  const handlePreviewBulletin = async () => {
    setPreviewingBulletin(true);
    try {
      const response = await axios.post(
        `${API}/ftpe-adhesion/workflow/preview-bulletin`,
        { form_data: formData },
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const win = window.open(url, '_blank');
      if (!win) {
        // Popup bloquée — fallback : déclencher un téléchargement
        const link = document.createElement('a');
        link.href = url;
        link.download = 'previsualisation_bulletin_ftpe.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      // Libération différée de l'objet URL (laisser le temps au nouvel onglet de charger)
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);

      toast.success('Prévisualisation générée');
    } catch (err) {
      // err.response.data est un Blob (responseType:'blob') → tenter d'en extraire le détail JSON
      let detail = 'Erreur lors de la génération de la prévisualisation';
      try {
        if (err?.response?.data instanceof Blob) {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.detail) detail = parsed.detail;
        }
      } catch { /* fallback message */ }
      toast.error(detail);
    } finally {
      setPreviewingBulletin(false);
    }
  };

  // Étape 3: Soumettre le formulaire
  const handleSubmitForm = async () => {
    // Validation basique
    const required = ['raison_sociale', 'siret', 'adresse', 'telephone', 'email', 'representant_nom'];
    const missing = required.filter(f => !formData[f]);
    
    if (missing.length > 0) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/ftpe-adhesion/workflow/create`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setAdhesionId(response.data.adhesion_id);
        toast.success('Bulletin d\'adhésion généré !');
        setCurrentStep(4);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création');
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  // Étape 4: Envoyer SMS OTP
  const handleSendOTP = async () => {
    if (!adhesionId) return;
    
    setOtpLoading(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/ftpe-adhesion/workflow/${adhesionId}/send-sms`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setOtpSent(true);
      if (response.data.debug_code) {
        setDebugCode(response.data.debug_code);
      }
      toast.success(response.data.message);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi du SMS');
    } finally {
      setOtpLoading(false);
    }
  };

  // Étape 4: Valider la signature
  const handleValidateSignature = async () => {
    if (!otpCode || !lieuSignature) {
      setError('Veuillez remplir le code et le lieu de signature');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/ftpe-adhesion/workflow/${adhesionId}/validate-signature`,
        { otp_code: otpCode, lieu_signature: lieuSignature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Signature validée !');
        setCurrentStep(5);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Code incorrect');
    } finally {
      setLoading(false);
    }
  };

  // Étape 5: Créer la session de paiement
  const handlePayment = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/ftpe-adhesion/workflow/${adhesionId}/create-payment`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la création du paiement');
    } finally {
      setLoading(false);
    }
  };

  // Télécharger le bulletin
  const handleDownloadBulletin = async () => {
    if (!adhesionId) return;
    
    try {
      const response = await axios.get(
        `${API}/ftpe-adhesion/workflow/${adhesionId}/document`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulletin_adhesion_ftpe_${adhesionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const progress = (currentStep / WORKFLOW_STEPS.length) * 100;
  const estimatedTime = getEstimatedTimeRemaining();
  const progressPercentage = getProgressPercentage();
  return { STEP_DURATION, adhesionId, adhesionIdFromUrl, autoSaveEnabled, checkPaymentStatus, currentStep, debugCode, draftId, draftIdFromUrl, error, estimatedTime, forMemberMode, formData, formModified, getEstimatedTimeRemaining, getProgressPercentage, handleDownloadBulletin, handleDownloadDocument, handlePayment, handlePreviewBulletin, handleSaveDraft, handleSendOTP, handleSubmitForm, handleValidateSignature, lastSaved, lieuSignature, loadDraft, loading, memberEmailFromUrl, memberEntrepriseFromUrl, memberIdFromUrl, memberNameFromUrl, memberPhoneFromUrl, memberTerritoireFromUrl, navigate, otpCode, otpLoading, otpSent, paymentStatus, previewingBulletin, progress, progressPercentage, savingDraft, searchParams, sessionIdFromUrl, setAdhesionId, setAutoSaveEnabled, setCurrentStep, setDebugCode, setDraftId, setError, setFormData, setFormModified, setLastSaved, setLieuSignature, setLoading, setOtpCode, setOtpLoading, setOtpSent, setPaymentStatus, setPreviewingBulletin, setSavingDraft, stepFromUrl, token, updateFormData, user };
}
